import React, { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import { Box, Button, Text, Flex } from '../index';
import { PhoneNumber } from '../masks/phone-number-mask';
import { CNPJMask, CEPMask, CPFMask } from '../masks/document-masks';
import {
  validateCNPJ,
  validateCPF,
  validateEmail,
  validateCEP,
  validatePhone,
  brazilianStates,
} from '../../utils/validation';

const FormSection = styled(Box)`
  background: white;
  border-radius: 0;
  padding: 0;
  margin-bottom: 32px;
  box-shadow: none;
`;

const SectionTitle = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #333;
  border-bottom: none;
  padding-bottom: 0;
`;

const FormRow = styled(Flex)`
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
`;

const InputWrapper = styled(Box)`
  flex: 1;
  min-width: 200px;

  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

const Label = styled.label`
  display: block;
  font-weight: 400;
  margin-bottom: 6px;
  color: #666;
  font-size: 13px;

  span.required {
    color: #e74c3c;
    margin-left: 2px;
  }
`;

const StyledInput = styled(Field)`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => (props.error ? '#e74c3c' : '#dfe6e9')};
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #3498db;
  }

  &:disabled {
    background-color: #f5f6fa;
    cursor: not-allowed;
  }
`;

const StyledSelect = styled(Field)`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => (props.error ? '#e74c3c' : '#dfe6e9')};
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
  cursor: pointer;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const ErrorMessage = styled(Text)`
  color: #e74c3c;
  font-size: 12px;
  margin-top: 4px;
`;

const HelperText = styled(Text)`
  color: #7f8c8d;
  font-size: 12px;
  margin-top: 4px;
  font-style: italic;
`;

const AlertBox = styled(Box)`
  padding: 16px;
  border-radius: 4px;
  margin-bottom: 24px;
  background-color: ${props => {
    if (props.type === 'success') return '#d4edda';
    if (props.type === 'error') return '#f8d7da';
    return '#d1ecf1';
  }};
  border: 1px solid
    ${props => {
      if (props.type === 'success') return '#c3e6cb';
      if (props.type === 'error') return '#f5c6cb';
      return '#bee5eb';
    }};
  border: 1px solid
    ${props => {
      if (props.type === 'success') return '#c3e6cb';
      if (props.type === 'error') return '#f5c6cb';
      return '#bee5eb';
    }};
  color: ${props => {
    if (props.type === 'success') return '#155724';
    if (props.type === 'error') return '#721c24';
    return '#0c5460';
  }};
`;

const SubmitButton = styled(Button)`
  background-color: #ff6b35;
  color: white;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover:not(:disabled) {
    background-color: #e65a2b;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

// Schema de validação
const DealerRegistrationSchema = Yup.object().shape({
  // Dados Pessoais
  firstName: Yup.string()
    .min(2, 'Nome deve ter pelo menos 2 caracteres')
    .required('Nome é obrigatório'),
  lastName: Yup.string()
    .min(2, 'Sobrenome deve ter pelo menos 2 caracteres')
    .required('Sobrenome é obrigatório'),
  email: Yup.string()
    .email('Email inválido')
    .test('email-validation', 'Email inválido', value => validateEmail(value))
    .required('Email é obrigatório'),
  phoneNumber: Yup.string()
    .test('phone-validation', 'Telefone inválido', value => validatePhone(value))
    .required('Telefone é obrigatório'),

  // Dados da Empresa
  cnpj: Yup.string()
    .test('cnpj-validation', 'CNPJ inválido', value => {
      if (!value) return true; // Se vazio, deixa o required validar
      const isValid = validateCNPJ(value);
      console.log('🔍 Validando CNPJ:', value, '→', isValid ? '✅ Válido' : '❌ Inválido');
      return isValid;
    })
    .required('CNPJ é obrigatório'),
  socialReason: Yup.string()
    .min(3, 'Razão Social deve ter pelo menos 3 caracteres')
    .required('Razão Social é obrigatória'),
  fantasyName: Yup.string()
    .min(3, 'Nome Fantasia deve ter pelo menos 3 caracteres')
    .required('Nome Fantasia é obrigatório'),
  legalNature: Yup.string().required('Natureza Jurídica é obrigatória'),

  // Dados do Representante Legal
  legalRepName: Yup.string()
    .min(3, 'Nome deve ter pelo menos 3 caracteres')
    .required('Nome do representante legal é obrigatório'),
  legalRepBirthDate: Yup.date()
    .max(new Date(), 'Data não pode ser futura')
    .required('Data de nascimento é obrigatória'),
  legalRepCpf: Yup.string()
    .test('cpf-validation', 'CPF inválido', value => {
      if (!value) return true; // Se vazio, deixa o required validar
      const isValid = validateCPF(value);
      console.log('🔍 Validando CPF:', value, '→', isValid ? '✅ Válido' : '❌ Inválido');
      return isValid;
    })
    .required('CPF do representante legal é obrigatório'),

  // Endereço
  zipcode: Yup.string()
    .test('cep-validation', 'CEP inválido', value => {
      if (!value) return true; // Se vazio, deixa o required validar
      const isValid = validateCEP(value);
      console.log('🔍 Validando CEP:', value, '→', isValid ? '✅ Válido' : '❌ Inválido');
      return isValid;
    })
    .required('CEP é obrigatório'),
  street: Yup.string()
    .min(3, 'Logradouro deve ter pelo menos 3 caracteres')
    .required('Logradouro é obrigatório'),
  number: Yup.string().required('Número é obrigatório'),
  complement: Yup.string(),
  neighborhood: Yup.string()
    .min(2, 'Bairro deve ter pelo menos 2 caracteres')
    .required('Bairro é obrigatório'),
  municipality: Yup.string()
    .min(2, 'Município deve ter pelo menos 2 caracteres')
    .required('Município é obrigatório'),
  state: Yup.string()
    .length(2, 'Estado deve ter 2 caracteres')
    .required('Estado é obrigatório'),

  // Atividades
  principalActivity: Yup.string().required('Atividade Principal é obrigatória'),
  secondaryActivities: Yup.string().required('Atividades Secundárias são obrigatórias'),

  // Campo oculto para ninja
  relatedNinja: Yup.string().email(),
});

export const DealerRegistrationForm = ({ initialValues = {}, onSubmit, status }) => {
  const defaultValues = React.useMemo(() => {
    const values = {
      // Dados Pessoais
      firstName: initialValues.firstName || '',
      lastName: initialValues.lastName || '',
      email: initialValues.email || '',
      phoneNumber: initialValues.phoneNumber || '',
      // Dados da Empresa
      cnpj: initialValues.cnpj || '',
      socialReason: initialValues.socialReason || '',
      fantasyName: initialValues.fantasyName || '',
      legalNature: initialValues.legalNature || '',
      // Dados do Representante Legal
      legalRepName: initialValues.legalRepName || '',
      legalRepBirthDate: initialValues.legalRepBirthDate || '',
      legalRepCpf: initialValues.legalRepCpf || '',
      // Endereço
      zipcode: initialValues.zipcode || '',
      street: initialValues.street || '',
      number: initialValues.number || '',
      complement: initialValues.complement || '',
      neighborhood: initialValues.neighborhood || '',
      municipality: initialValues.municipality || '',
      state: initialValues.state || '',
      // Atividades
      principalActivity: initialValues.principalActivity || '',
      secondaryActivities: initialValues.secondaryActivities || '',
      // Sistema (oculto)
      relatedNinja: initialValues.relatedNinja || 'felipe.nascimento@instacarro.com',
    };
    
    console.log('defaultValues recalculado:', values);
    return values;
  }, [initialValues]);

  return (
    <Box>
      {status?.success && (
        <AlertBox type="success">
          <strong>Cadastro realizado com sucesso!</strong>
          <br />
          Entraremos em contato em breve.
        </AlertBox>
      )}

      {status?.error && (
        <AlertBox type="error">
          <strong>Erro ao realizar cadastro</strong>
          <br />
          {status.message || 'Por favor, verifique os dados e tente novamente.'}
        </AlertBox>
      )}

      <Formik
        initialValues={defaultValues}
        validationSchema={DealerRegistrationSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ errors, touched, isSubmitting, values, setFieldValue, handleChange, handleBlur }) => {
          // UseEffect para forçar atualização do telefone quando initialValues mudar
          React.useEffect(() => {
            if (initialValues.phoneNumber && values.phoneNumber !== initialValues.phoneNumber) {
              setFieldValue('phoneNumber', initialValues.phoneNumber);
            }
          }, [initialValues.phoneNumber]);

          return (
          <Form>
            {/* Seção 1: Dados Pessoais */}
            <FormSection>
              <SectionTitle>Dados Pessoais do Responsável</SectionTitle>

              <FormRow>
                <InputWrapper>
                  <Label htmlFor="firstName">
                    Nome <span className="required">*</span>
                  </Label>
                  <StyledInput
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="João"
                    error={errors.firstName && touched.firstName}
                  />
                  {errors.firstName && touched.firstName && (
                    <ErrorMessage>{errors.firstName}</ErrorMessage>
                  )}
                </InputWrapper>

                <InputWrapper>
                  <Label htmlFor="lastName">
                    Sobrenome <span className="required">*</span>
                  </Label>
                  <StyledInput
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Silva"
                    error={errors.lastName && touched.lastName}
                  />
                  {errors.lastName && touched.lastName && (
                    <ErrorMessage>{errors.lastName}</ErrorMessage>
                  )}
                </InputWrapper>
              </FormRow>

              <FormRow>
                <InputWrapper>
                  <Label htmlFor="email">
                    Email <span className="required">*</span>
                  </Label>
                  <StyledInput
                    type="email"
                    id="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="joao.silva@loja.com.br"
                    error={errors.email && touched.email}
                  />
                  {errors.email && touched.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                </InputWrapper>

                <InputWrapper>
                  <Label htmlFor="phoneNumber">
                    Telefone <span className="required">*</span>
                  </Label>
                  <Field name="phoneNumber">
                    {({ field }) => (
                      <StyledInput
                        as={PhoneNumber}
                        {...field}
                        id="phoneNumber"
                        placeholder="(11) 98765-4321"
                        value={field.value}
                        onChange={(e) => setFieldValue('phoneNumber', e.target.value)}
                        error={errors.phoneNumber && touched.phoneNumber}
                      />
                    )}
                  </Field>
                  {errors.phoneNumber && touched.phoneNumber && (
                    <ErrorMessage>{errors.phoneNumber}</ErrorMessage>
                  )}
                </InputWrapper>
              </FormRow>
            </FormSection>

            {/* Seção 2: Dados da Empresa */}
            <FormSection>
              <SectionTitle>Dados da Empresa</SectionTitle>

              <FormRow>
                <InputWrapper>
                  <Label htmlFor="cnpj">
                    CNPJ <span className="required">*</span>
                  </Label>
                  <Field name="cnpj">
                    {({ field }) => (
                      <StyledInput
                        {...field}
                        as={CNPJMask}
                        id="cnpj"
                        placeholder="00.000.000/0001-00"
                        error={errors.cnpj && touched.cnpj}
                      />
                    )}
                  </Field>
                  {errors.cnpj && touched.cnpj && <ErrorMessage>{errors.cnpj}</ErrorMessage>}
                </InputWrapper>

                <InputWrapper>
                  <Label htmlFor="legalNature">
                    Natureza Jurídica <span className="required">*</span>
                  </Label>
                  <StyledInput
                    type="text"
                    id="legalNature"
                    name="legalNature"
                    value={values.legalNature}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Sociedade Empresária Limitada"
                    error={errors.legalNature && touched.legalNature}
                  />
                  {errors.legalNature && touched.legalNature && (
                    <ErrorMessage>{errors.legalNature}</ErrorMessage>
                  )}
                </InputWrapper>
              </FormRow>

              <FormRow>
                <InputWrapper>
                  <Label htmlFor="socialReason">
                    Razão Social <span className="required">*</span>
                  </Label>
                  <StyledInput
                    type="text"
                    id="socialReason"
                    name="socialReason"
                    value={values.socialReason}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Auto Peças Silva LTDA"
                    error={errors.socialReason && touched.socialReason}
                  />
                  {errors.socialReason && touched.socialReason && (
                    <ErrorMessage>{errors.socialReason}</ErrorMessage>
                  )}
                </InputWrapper>

                <InputWrapper>
                  <Label htmlFor="fantasyName">
                    Nome Fantasia <span className="required">*</span>
                  </Label>
                  <StyledInput
                    type="text"
                    id="fantasyName"
                    name="fantasyName"
                    value={values.fantasyName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Silva Auto Peças"
                    error={errors.fantasyName && touched.fantasyName}
                  />
                  {errors.fantasyName && touched.fantasyName && (
                    <ErrorMessage>{errors.fantasyName}</ErrorMessage>
                  )}
                </InputWrapper>
              </FormRow>
            </FormSection>

            {/* Seção 3: Dados do Representante Legal */}
            <FormSection>
              <SectionTitle>Dados do Representante Legal</SectionTitle>

              <FormRow>
                <InputWrapper>
                  <Label htmlFor="legalRepName">
                    Nome Completo <span className="required">*</span>
                  </Label>
                  <StyledInput
                    type="text"
                    id="legalRepName"
                    name="legalRepName"
                    value={values.legalRepName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="João da Silva"
                    error={errors.legalRepName && touched.legalRepName}
                  />
                  {errors.legalRepName && touched.legalRepName && (
                    <ErrorMessage>{errors.legalRepName}</ErrorMessage>
                  )}
                </InputWrapper>
              </FormRow>

              <FormRow>
                <InputWrapper>
                  <Label htmlFor="legalRepBirthDate">
                    Data de Nascimento <span className="required">*</span>
                  </Label>
                  <StyledInput
                    type="date"
                    id="legalRepBirthDate"
                    name="legalRepBirthDate"
                    value={values.legalRepBirthDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    max={new Date().toISOString().split('T')[0]}
                    error={errors.legalRepBirthDate && touched.legalRepBirthDate}
                  />
                  {errors.legalRepBirthDate && touched.legalRepBirthDate && (
                    <ErrorMessage>{errors.legalRepBirthDate}</ErrorMessage>
                  )}
                </InputWrapper>

                <InputWrapper>
                  <Label htmlFor="legalRepCpf">
                    CPF <span className="required">*</span>
                  </Label>
                  <Field name="legalRepCpf">
                    {({ field }) => (
                      <StyledInput
                        {...field}
                        as={CPFMask}
                        id="legalRepCpf"
                        placeholder="000.000.000-00"
                        error={errors.legalRepCpf && touched.legalRepCpf}
                      />
                    )}
                  </Field>
                  {errors.legalRepCpf && touched.legalRepCpf && (
                    <ErrorMessage>{errors.legalRepCpf}</ErrorMessage>
                  )}
                </InputWrapper>
              </FormRow>
            </FormSection>

            {/* Seção 3: Dados do Representante Legal */}
            <FormSection>
              <SectionTitle>Dados do Representante Legal</SectionTitle>

              <FormRow>
                <InputWrapper>
                  <Label htmlFor="legalRepName">
                    Nome Completo <span className="required">*</span>
                  </Label>
                  <StyledInput
                    type="text"
                    id="legalRepName"
                    name="legalRepName"
                    placeholder="José da Silva Santos"
                    error={errors.legalRepName && touched.legalRepName}
                  />
                  {errors.legalRepName && touched.legalRepName && (
                    <ErrorMessage>{errors.legalRepName}</ErrorMessage>
                  )}
                </InputWrapper>
              </FormRow>

              <FormRow>
                <InputWrapper>
                  <Label htmlFor="legalRepBirthDate">
                    Data de Nascimento <span className="required">*</span>
                  </Label>
                  <StyledInput
                    type="date"
                    id="legalRepBirthDate"
                    name="legalRepBirthDate"
                    max={new Date().toISOString().split('T')[0]}
                    error={errors.legalRepBirthDate && touched.legalRepBirthDate}
                  />
                  {errors.legalRepBirthDate && touched.legalRepBirthDate && (
                    <ErrorMessage>{errors.legalRepBirthDate}</ErrorMessage>
                  )}
                </InputWrapper>

                <InputWrapper>
                  <Label htmlFor="legalRepCpf">
                    CPF <span className="required">*</span>
                  </Label>
                  <StyledInput
                    as={CPFMask}
                    id="legalRepCpf"
                    name="legalRepCpf"
                    placeholder="000.000.000-00"
                    error={errors.legalRepCpf && touched.legalRepCpf}
                  />
                  {errors.legalRepCpf && touched.legalRepCpf && (
                    <ErrorMessage>{errors.legalRepCpf}</ErrorMessage>
                  )}
                </InputWrapper>
              </FormRow>
            </FormSection>

            {/* Seção 4: Endereço */}
            <FormSection>
              <SectionTitle>Endereço da Empresa</SectionTitle>

              <FormRow>
                <InputWrapper style={{ maxWidth: '200px' }}>
                  <Label htmlFor="zipcode">
                    CEP <span className="required">*</span>
                  </Label>
                  <Field name="zipcode">
                    {({ field }) => (
                      <StyledInput
                        {...field}
                        as={CEPMask}
                        id="zipcode"
                        placeholder="01310-100"
                        error={errors.zipcode && touched.zipcode}
                      />
                    )}
                  </Field>
                  {errors.zipcode && touched.zipcode && (
                    <ErrorMessage>{errors.zipcode}</ErrorMessage>
                  )}
                </InputWrapper>
              </FormRow>

              <FormRow>
                <InputWrapper style={{ flex: 3 }}>
                  <Label htmlFor="street">
                    Logradouro <span className="required">*</span>
                  </Label>
                  <StyledInput
                    type="text"
                    id="street"
                    name="street"
                    value={values.street}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Rua das Flores"
                    error={errors.street && touched.street}
                  />
                  {errors.street && touched.street && <ErrorMessage>{errors.street}</ErrorMessage>}
                </InputWrapper>

                <InputWrapper style={{ flex: 1, maxWidth: '150px' }}>
                  <Label htmlFor="number">
                    Número <span className="required">*</span>
                  </Label>
                  <StyledInput
                    type="text"
                    id="number"
                    name="number"
                    value={values.number}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="123"
                    error={errors.number && touched.number}
                  />
                  {errors.number && touched.number && <ErrorMessage>{errors.number}</ErrorMessage>}
                </InputWrapper>
              </FormRow>

              <FormRow>
                <InputWrapper>
                  <Label htmlFor="neighborhood">
                    Bairro <span className="required">*</span>
                  </Label>
                  <StyledInput
                    type="text"
                    id="neighborhood"
                    name="neighborhood"
                    value={values.neighborhood}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Centro"
                    error={errors.neighborhood && touched.neighborhood}
                  />
                  {errors.neighborhood && touched.neighborhood && (
                    <ErrorMessage>{errors.neighborhood}</ErrorMessage>
                  )}
                </InputWrapper>

                <InputWrapper>
                  <Label htmlFor="complement">Complemento</Label>
                  <StyledInput
                    type="text"
                    id="complement"
                    name="complement"
                    value={values.complement}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Sala 302"
                  />
                  <HelperText>Opcional</HelperText>
                </InputWrapper>
              </FormRow>

              <FormRow>
                <InputWrapper>
                  <Label htmlFor="municipality">
                    Município <span className="required">*</span>
                  </Label>
                  <StyledInput
                    type="text"
                    id="municipality"
                    name="municipality"
                    value={values.municipality}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="São Paulo"
                    error={errors.municipality && touched.municipality}
                  />
                  {errors.municipality && touched.municipality && (
                    <ErrorMessage>{errors.municipality}</ErrorMessage>
                  )}
                </InputWrapper>

                <InputWrapper style={{ maxWidth: '150px' }}>
                  <Label htmlFor="state">
                    UF <span className="required">*</span>
                  </Label>
                  <StyledSelect
                    as="select"
                    id="state"
                    name="state"
                    value={values.state}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.state && touched.state}
                  >
                    <option value="">Selecione</option>
                    {brazilianStates.map(state => (
                      <option key={state.value} value={state.value}>
                        {state.value} - {state.label}
                      </option>
                    ))}
                  </StyledSelect>
                  {errors.state && touched.state && <ErrorMessage>{errors.state}</ErrorMessage>}
                </InputWrapper>
              </FormRow>
            </FormSection>

            {/* Seção 5: Atividades */}
            <FormSection>
              <SectionTitle>Atividades da Empresa</SectionTitle>

              <FormRow>
                <InputWrapper>
                  <Label htmlFor="principalActivity">
                    Atividade Principal <span className="required">*</span>
                  </Label>
                  <StyledInput
                    type="text"
                    id="principalActivity"
                    name="principalActivity"
                    value={values.principalActivity}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Comércio de veículos automotores"
                    error={errors.principalActivity && touched.principalActivity}
                  />
                  {errors.principalActivity && touched.principalActivity && (
                    <ErrorMessage>{errors.principalActivity}</ErrorMessage>
                  )}
                </InputWrapper>
              </FormRow>

              <FormRow>
                <InputWrapper>
                  <Label htmlFor="secondaryActivities">
                    Atividades Secundárias <span className="required">*</span>
                  </Label>
                  <StyledInput
                    as="textarea"
                    id="secondaryActivities"
                    name="secondaryActivities"
                    value={values.secondaryActivities}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Manutenção e reparação de veículos"
                    rows={3}
                    error={errors.secondaryActivities && touched.secondaryActivities}
                  />
                  {errors.secondaryActivities && touched.secondaryActivities && (
                    <ErrorMessage>{errors.secondaryActivities}</ErrorMessage>
                  )}
                  <HelperText>Separe múltiplas atividades por vírgula</HelperText>
                </InputWrapper>
              </FormRow>
            </FormSection>

            {/* Botões de ação */}
            <Box mt={4} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <SubmitButton
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Cadastrando...' : 'Finalizar Cadastro'}
              </SubmitButton>
            </Box>
          </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default DealerRegistrationForm;
